import { Snackbar } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ReactComponent as AttachmentIcon } from '../../assets/icons/attachment.svg';
import useFileUploadWithValidation, {
    UploadedFile
} from '../../hooks/useFileUploadWithValidation';
import { generateUniqueId } from '../../utils/attachments';
import color from '../../theme/color';
import AttachmentItem from '../AttachmentItem';
import UploadProgress from '../UploadProgress';
import S from './styled';

interface Props {
    attachmentIds: number[];
    setAttachmentIds: React.Dispatch<React.SetStateAction<number[]>>;
    title?: string;
    instructions?: string;
    compact?: boolean;
}

const AttachmentUpload: React.FC<Props> = ({
    attachmentIds: _attachmentIds,
    setAttachmentIds,
    title,
    instructions,
    compact = false
}) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesSelect = useFileUploadWithValidation({
        onFilesUpdate: setFiles,
        onError: setFileError,
        onFileComplete: (fileId) => {
            setAttachmentIds((prevIds) => [...prevIds, fileId]);
        },
        onFileRemove: (fileId) => {
            setAttachmentIds((prevIds) =>
                prevIds.filter((prevId) => prevId !== fileId)
            );
        }
    });

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const selectedFiles = Array.from(e.target.files).filter(
                    (file) =>
                        file.type.startsWith('image/') ||
                        file.type.startsWith('video/') ||
                        file.type === 'application/pdf' ||
                        file.type === 'application/msword' ||
                        file.type ===
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                        file.type === 'application/vnd.ms-excel' ||
                        file.type ===
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                        file.type === 'text/csv' ||
                        file.type === 'text/plain' ||
                        file.type === 'application/vnd.ms-powerpoint' ||
                        file.type ===
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                );

                if (selectedFiles.length > 0) {
                    const selectedFilesWithIds = selectedFiles.map((file) => ({
                        file,
                        id: generateUniqueId()
                    }));
                    handleFilesSelect(selectedFilesWithIds);
                }
            }
            // Reset the input so the same file can be selected again
            if (e.target) {
                e.target.value = '';
            }
        },
        [handleFilesSelect]
    );

    const handleAttachmentClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const removeAttachment = useCallback(
        (idToRemove: number): void => {
            const fileToRemove = files.find(
                (file) => file.fileId === idToRemove
            );

            if (fileToRemove && fileToRemove.fileId) {
                setAttachmentIds((prevIds) =>
                    prevIds.filter(
                        (prevId) => prevId !== Number(fileToRemove.fileId)
                    )
                );
            }

            setFiles((prevFiles) =>
                prevFiles.filter((file) => file.fileId !== idToRemove)
            );
        },
        [files, setAttachmentIds]
    );

    const cancelUpload = useCallback(
        (fileId: number): void => {
            const fileToCancel = files.find((f) => f.id === fileId);

            if (fileToCancel && fileToCancel.uploadController) {
                fileToCancel.uploadController.abort();
            }

            removeAttachment(fileId);
        },
        [files, removeAttachment]
    );

    useEffect(() => {
        const handlePaste = async (event: ClipboardEvent): Promise<void> => {
            const clipboardItems = event.clipboardData?.items;
            if (!clipboardItems) {
                return;
            }

            const mediaFiles: File[] = [];

            for (let i = 0; i < clipboardItems.length; i++) {
                const item = clipboardItems[i];
                if (
                    item.type.startsWith('image/') ||
                    item.type.startsWith('video/')
                ) {
                    const file = item.getAsFile();
                    if (file) {
                        const MAX_FILE_SIZE = 20 * 1024 * 1024;
                        if (file.size <= MAX_FILE_SIZE) {
                            mediaFiles.push(file);
                        } else {
                            alert(
                                'File from clipboard was not added because it exceeds the 20MB size limit.'
                            );
                        }
                    }
                }
            }

            if (mediaFiles.length > 0) {
                event.preventDefault();
                const filesWithIds = mediaFiles.map((file) => ({
                    file,
                    id: generateUniqueId()
                }));
                await handleFilesSelect(filesWithIds);
            }
        };

        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [handleFilesSelect]);

    const defaultTitle = 'Attachments';
    const defaultInstructions = '';
    const Root = compact ? S.CompactRoot : S.Root;

    return (
        <Root>
            <S.HiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                multiple
                onChange={handleFileChange}
            />
            {!compact && (
                <>
                    <Typography sx={S.labelTitle} component="label">
                        {title || defaultTitle}
                        <S.TitleExclamation>!</S.TitleExclamation>
                    </Typography>
                    <Typography sx={S.labelInstructions} component="label">
                        {instructions || defaultInstructions}
                    </Typography>
                </>
            )}
            <S.AttachmentsContainer>
                <S.AttachmentsList>
                    <S.AttachmentButton onClick={handleAttachmentClick}>
                        <AttachmentIcon color={color.lightningYellow} />
                    </S.AttachmentButton>
                    {files.map((attachedFile) => (
                        <AttachmentItem
                            key={`attachment-${attachedFile.id}`}
                            file={{
                                id:
                                    Number(attachedFile.fileId) ||
                                    generateUniqueId(),
                                name: attachedFile.file.name,
                                type: attachedFile.file.type
                            }}
                            index={Number(attachedFile.fileId)}
                            removeAttachment={removeAttachment}
                        />
                    ))}
                </S.AttachmentsList>
            </S.AttachmentsContainer>

            <UploadProgress files={files} onCancelUpload={cancelUpload} />

            <Snackbar
                open={!!fileError}
                autoHideDuration={6000}
                onClose={() => setFileError(null)}
                message={fileError}
            />
        </Root>
    );
};

export default AttachmentUpload;
